/**
 * Data hook behind the meetverse live pill row (recap/pill redesign §1; live +
 * starting-soon since T-12; upcoming since Dexter's 2026-07-27 ruling).
 *
 * TWO fetches, one merged list (Dexter's split-fetch ruling on PR #1035; the
 * bands are backend #730). Both hit the PUBLIC `GET /events/live`, both under
 * their own key, both with the same `railStaleTimeMs` and the same
 * `liveEvents` kill switch — which gates the fetches AND the tick:
 *
 *   A. `eventsLiveNowKey()` — the plain call: live rows going-desc ("most-alive
 *      rooms first"), then the starting-soon set soonest-first (`end > now AND
 *      start ≤ now + window`; that window is a server-side lever, default
 *      120min, mirrored by `LIVE_EVENTS_CONFIG.soonLeadMs` — change both
 *      together).
 *   B. `eventsUpcomingTailKey()` — `only_upcoming: true`: ONLY the band beyond
 *      that soon window, soonest-first, horizon-bounded server-side.
 *
 * Deliberately NO auth gate on either: the endpoint serves guests (host + 3
 * `preview_users`) and members (≤5 faces + their visibility extras) alike, so
 * the row works signed-out — the ONLY live-layer cache that does.
 *
 * WHY two calls instead of one widened page: on a single call the tail shares
 * page 1 with live+soon, so a busy meetverse fills that page with live rows and
 * starves the upcoming tail off it entirely — a signed-out visitor sees ZERO
 * upcoming pills precisely when the platform is busiest, with nothing erroring.
 * A bigger page only moves the threshold (20 is the server's hard cap) while
 * shipping 20 fully-enriched events with avatar stacks to every signed-out home
 * page load to render at most three upcoming pills. Split, each band gets its
 * own small page and neither can crowd the other out.
 *
 * MERGE SEAM: A's rows first, in A's server order, then B's — DEDUPED BY ID
 * against A's payload. A wins, and A's ids are A's whether or not A's own phase
 * filter kept them. The two bands tile per-instant, but the two queries refetch
 * independently, so the seam is where they can disagree. One dedupe covers the
 * two cases that matter:
 *   - SKEW: an upcoming row whose start crossed the soon seam between the two
 *     fetches sits in BOTH payloads. Deduped, it renders once, in A's block.
 *     (The mirror case — only B still carries it — needs no handling: phase is
 *     re-derived below from the row's own dates, never from which call
 *     delivered it.)
 *   - ROLLBACK: an older backend doesn't recognize `only_upcoming`, ignores it,
 *     and answers B with the SAME live+soon page as A. Every B id is already an
 *     A id — B's page is a strict PREFIX of A's, which is exactly why
 *     `MEETVERSE_UPCOMING_TAIL_PER_PAGE` must stay ≤ `MEETVERSE_LIVE_PER_PAGE`
 *     (see that constant's ceiling note) — so B contributes zero net rows and
 *     the row renders exactly its pre-split self. The dedupe is what makes that
 *     true: required safety, not an optimization.
 * A third case exists and is ACCEPTED, not handled: when B is the fresher of
 * the two across a crossing, a row can fall out of BOTH bands — A predates the
 * crossing so its band excluded the row, B postdates it so its band excluded it
 * too — and the row is absent until A refetches. Both queries mount together,
 * share `staleTime` and share every invalidation trigger, so that window is
 * sub-second per event, and its cost is the same staleness a single call had
 * before the split.
 *
 * NO CAP HERE (Dexter's review of PR #1035): this hook keeps EVERY in-window
 * row — live, soon and upcoming alike — in merge order. The glanceable-rail
 * cap on the far-future tail is `LIVE_EVENTS_CONFIG.meetverseUpcomingMax`,
 * applied by the RENDER layer (`LiveMeetversePills`) after its personal-rail
 * dedupe and own-event filter. Capping here ran BEFORE those filters, so a
 * member whose personal rail already held the 3 nearest upcoming events saw
 * the public row's upcoming set emptied while rows 4-6 sat discarded in this
 * very payload. Keeping them means the render layer always has a full tail to
 * fill from.
 *
 * Server truth ages between refetches (`railStaleTimeMs`): an event that
 * ended since the fetch must leave, a soon event whose start passed must flip
 * to a live pill, and an upcoming event whose start enters the soon window
 * must flip too — all within one tick, not one refetch — so each row's phase
 * is re-derived against a `useNow` tick (the rail idiom; entries carry the
 * derived phase) and rows outside every live/soon/upcoming window
 * (`ended-recap`/`none`) are dropped. Order stays each call's SERVER order, no
 * client comparator — unlike `useLiveEventsRail`, whose calendar-ordered source
 * needs one: a client-side soon→live flip always happens at the live/soon seam
 * (the server sorts soon rows soonest-first, so the earliest-starting soon row
 * is the one that flips), keeping the live block contiguous; the next refetch
 * restores going-desc within it. Call this from an isolated leaf (the row
 * component), never at feed level, so the 30s tick re-renders only the row
 * subtree (spec §3).
 *
 * CAVEAT (Dexter's "Keep server ranking" ruling, 2026-07-28): "no client
 * comparator" describes THIS hook's own output only. `LiveMeetversePills`, the
 * one consumer that merges these entries with a second source, re-tiers the
 * merged row client-side by phase and going (`compareEntriesByPhaseAndGoingOnly`)
 * — but it preserves THIS hook's server order untouched inside each of those
 * groups, which is the whole point: the busiest-first ranking below survives end
 * to end for any two entries that land in the same group. Every entry THIS hook
 * produces is `is_going === false` by the time it reaches that merge
 * (`LiveMeetversePills`' own-event filter drops any that aren't), so "same
 * group" collapses to "same phase" for this hook's own rows — the busiest-first
 * order is never at risk of being cut across by the going dimension.
 *
 * Identity contract (the rail's `useStableBySignature` idiom), spanning the
 * MERGED list so a change in either call reaches it: ticks or refetches that
 * change nothing row-relevant — id set + order, phase (dot, copy shape, faces
 * gate), `is_going` (the soon goer/non-goer copy branch), going count (+N
 * chip), preview id+name+picture (the faces), start_date (the soon countdown),
 * name (the pill label) — return the SAME array reference.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLiveEvents } from '@api-client/api-client';
import { featureFlags } from '@config/featureFlags';
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
// Type-only: the canonical `{ event, phase }` entry shape of the live pill rows.
import type { LiveRailEntry } from '@hooks/useLiveEventsRail';
import { useNow } from '@hooks/useNow';
import { useStableBySignature } from '@hooks/useStableBySignature';
import { eventsLiveNowKey, eventsUpcomingTailKey } from '@lib/cache/events';
import type { EventRead } from '@models/api';
import { getEventGoingCount } from '@utils/eventGoingCount';
import { getEventPhase } from '@utils/eventLiveness';

/** The row is a compact pill rail — the server default page is plenty (§1: per_page ≤ 20).
 *  It no longer has to carry the upcoming tail as well: that band has its own
 *  call, so a busy live/soon set can't crowd it out of this one.
 *  Exported for the test that pins the tail-page ceiling below. */
export const MEETVERSE_LIVE_PER_PAGE = 10;

/** The upcoming tail's own page. The RENDER layer caps upcoming entries at
 *  `LIVE_EVENTS_CONFIG.meetverseUpcomingMax` (3); the extra rows are headroom
 *  for the two filters that run BEFORE that cap in `LiveMeetversePills` — the
 *  id dedupe against the viewer's personal rail and the `is_going` own-event
 *  drop — so a member whose nearest upcoming events are all their own still
 *  gets three public ones. Six covers that at a fraction of a full page.
 *
 *  CEILING — this MUST stay ≤ `MEETVERSE_LIVE_PER_PAGE`. The rollback promise
 *  in the docstring above (an old backend ignoring `only_upcoming` contributes
 *  zero net rows) holds because both calls ask for `page: 1` under one
 *  deterministic server order, so B's page is then a strict PREFIX of A's and
 *  the seam dedupe eats all of it. Raise this above the live page and B starts
 *  returning live+soon rows A never carried — they sail through the dedupe and
 *  appear as extra pills on exactly the path documented as "renders exactly its
 *  pre-split self". Need more headroom? Raise BOTH, or wait for #730 to be
 *  irreversible. Pinned by a test in `useMeetverseLiveEvents.test.tsx`. */
export const MEETVERSE_UPCOMING_TAIL_PER_PAGE = 6;

/** Stable identity for every empty path (disabled / loading / none in window). */
const EMPTY_ENTRIES: LiveRailEntry[] = [];

/** The phase this row renders as at `now`, or `null` when its window has closed
 *  since the fetch (`ended-recap`/`none`) and the row must drop it. Shared by
 *  both calls' merge loops so the two can't drift on what "in window" means. */
const railPhase = (event: EventRead, now: Date): LiveRailEntry['phase'] | null => {
  const phase = getEventPhase(event, now);
  return phase === 'live' || phase === 'soon' || phase === 'upcoming' ? phase : null;
};

/** `encodeURIComponent` leaves `~` unescaped (it's an RFC 3986 "unreserved"
 *  character), but `~` is `previewUserSignature`'s field separator below: a
 *  raw `~` in a free-text name would otherwise forge a fake separator and let
 *  two different name/picture pairs collide onto the same signature. Escape
 *  it explicitly on top of `encodeURIComponent`. */
const encodeSignatureField = (value: string): string =>
  encodeURIComponent(value).replace(/~/g, '%7E');

/** Signature segment for one preview user. Encodes name and picture, not just
 *  id: a field the row renders but the signature omits silently stops
 *  updating mid-live (the #967 rename lesson; user.updated makes identity
 *  changes reachable, so the faces must re-render on them too). */
export const previewUserSignature = (user: {
  id: string;
  full_name: string;
  profile_picture: string | null;
}): string =>
  `${user.id}~${encodeSignatureField(user.full_name)}~${encodeSignatureField(user.profile_picture ?? '')}`;

export interface UseMeetverseLiveEventsResult {
  /**
   * EVERY in-window event of BOTH payloads — live + starting-soon + upcoming,
   * uncapped — each with its tick-derived phase, in merge order: the live+soon
   * call's server order first, then the upcoming tail's, deduped by id against
   * the first. Empty when the meetverse is quiet. The row that renders these
   * caps the upcoming tail itself (`meetverseUpcomingMax`), after its own dedupe.
   */
  entries: LiveRailEntry[];
  /** The tick the phases were derived against (one clock per surface tree, W-9). */
  now: Date;
}

export function useMeetverseLiveEvents(): UseMeetverseLiveEventsResult {
  // Kill switch only — NO auth in the gate (§1: the row is public, guests fetch too).
  const enabled = featureFlags.liveEvents;
  // Primitive tick value drives phase recomputation without a non-primitive dep
  // (house rule); `enabled` reaches the tick so the kill switch stops the timer.
  const nowMs = useNow(LIVE_EVENTS_CONFIG.nowTickMs, enabled).getTime();

  // Call A — live + starting-soon, in the server's live-first order.
  const { data: liveData } = useQuery({
    queryKey: eventsLiveNowKey(),
    queryFn: async () => {
      const response = await getLiveEvents({ page: 1, per_page: MEETVERSE_LIVE_PER_PAGE });
      return response.data.data;
    },
    enabled,
    staleTime: LIVE_EVENTS_CONFIG.railStaleTimeMs,
  });

  // Call B — the upcoming band ONLY, soonest-first, so no amount of live/soon
  // activity can push the tail off the page (see WHY two calls, above).
  const { data: upcomingTailData } = useQuery({
    queryKey: eventsUpcomingTailKey(),
    queryFn: async () => {
      const response = await getLiveEvents({
        page: 1,
        per_page: MEETVERSE_UPCOMING_TAIL_PER_PAGE,
        only_upcoming: true,
      });
      return response.data.data;
    },
    enabled,
    staleTime: LIVE_EVENTS_CONFIG.railStaleTimeMs,
  });

  const { entries, signature } = useMemo(() => {
    if (!enabled) {
      return { entries: EMPTY_ENTRIES, signature: '' };
    }

    const now = new Date(nowMs);
    // Re-derive each row's phase at this tick; drop rows whose window closed
    // since the fetch. Each call's server order is kept — no sort (see module
    // docstring). EVERY in-window row survives, upcoming included: the render
    // layer caps the far-future tail after its own dedupe (NO CAP HERE note).
    const inWindow: LiveRailEntry[] = [];
    const liveRows = liveData ?? [];
    for (const event of liveRows) {
      const phase = railPhase(event, now);
      if (phase) {
        inWindow.push({ event, phase });
      }
    }

    // The merge seam. Call A's payload OWNS its ids — dropped rows included —
    // so a row that crossed the soon seam between the two independent refetches
    // renders once (in A's block), and an older backend that ignores
    // `only_upcoming` and answers B with A's live+soon page adds nothing at all.
    const liveIds = new Set(liveRows.map(event => event.id));
    for (const event of upcomingTailData ?? []) {
      if (liveIds.has(event.id)) {
        continue;
      }
      const phase = railPhase(event, now);
      if (phase) {
        inWindow.push({ event, phase });
      }
    }
    if (inWindow.length === 0) {
      return { entries: EMPTY_ENTRIES, signature: '' };
    }

    return {
      entries: inWindow,
      // Everything the row renders from: id set + order, phase (dot + copy
      // shape + faces gate), is_going (the soon goer/non-goer copy branch),
      // going count (the +N chip), preview id+name+picture (the faces),
      // start_date (the soon countdown copy), name (the pill label —
      // encoded so free text can't collide with the field/entry
      // separators). A field the row renders but the signature omits is a
      // field that silently stops updating mid-live (#967 review: renames
      // never reached the label). Ticks that change none of these keep the
      // previous identity.
      signature: inWindow
        .map(
          entry =>
            `${entry.event.id}:${entry.phase}:${entry.event.is_going}:${getEventGoingCount(entry.event)}:${(entry.event.preview_users ?? []).map(previewUserSignature).join(',')}:${entry.event.start_date}:${encodeURIComponent(entry.event.name)}`,
        )
        .join('|'),
    };
  }, [enabled, liveData, upcomingTailData, nowMs]);

  const stable = useStableBySignature(entries, signature);
  // One Date per tick (not per consumer render) — the W-9 shared clock.
  const now = useMemo(() => new Date(nowMs), [nowMs]);

  return useMemo(() => ({ entries: stable, now }), [stable, now]);
}
